const scrollElIntoView = function (
  ref: HTMLElement,
  axis: 'horizontal' | 'vertical',
  elOffsets: [number, number],
  elSize: number,
  padding: number,
): void {
  const scrollPosPropName = axis === 'vertical' ? 'scrollTop' : 'scrollLeft'
  const refSize = axis === 'vertical' ? ref.offsetHeight : ref.offsetWidth
  const elOuterSize = elSize + padding * 2

  // Too large to fit in the ref? Position it so as to fill the ref
  if (elOuterSize > refSize) {
    ref[scrollPosPropName] = elOffsets[0] + (elOuterSize - refSize) / 2
    return
  }

  const refScrollPos = ref[scrollPosPropName]

  // Align to top/left?
  let diff = refScrollPos - elOffsets[0] + padding
  if (diff > 0) {
    ref[scrollPosPropName] -= diff
    return
  }

  // Or align to bottom/right?
  diff = elOffsets[1] - (refScrollPos + refSize) + padding
  if (diff > 0) ref[scrollPosPropName] += diff

  // Or do nothing
}
const getElRefOffset = (
  el: HTMLElement,
  dir: 'top' | 'left' = 'top',
  ref: HTMLElement = document.body,
): number => {
  const offsetPosMethodName = dir !== 'left' ? 'offsetTop' : 'offsetLeft'
  let offset = el[offsetPosMethodName]

  while (el.offsetParent instanceof HTMLElement && el.offsetParent !== ref) {
    el = el.offsetParent
    offset += el[offsetPosMethodName]
  }

  return offset
}

const isEdge = (): boolean => window.navigator.userAgent.indexOf('Edge') > -1

/**
 * Note: reused from other project, will be good the refactor the logic and make it clearer
 *
 * Scroll elements into view, horizontally or vertically, when Element.scrollIntoView()
 * doesn't do exactly what we want (e.g. it doesn't always make an el entirely visible
 * if it already partly is).
 *
 * Takes parameters as an object:
 * - Mandatory:
 *   - el: the element to scroll into view (can optionally be replaced with
 *     param.offsets if these numbers are already known)
 *   - ref: the reference element (i.e. the one with the scrollbar) (must be
 *     a valid offset parent – body, table, th, td, or any positioned parent – when
 *     param.el isn't replaced with param.offsets)
 * - Optional:
 *   - axis: 'horizontal' or 'vertical' (default)
 *   - padding: padding to be left around param.el after scrolling (default: 0)
 */
type ScrollIntoViewParams = {
  el?: HTMLElement
  ref: HTMLElement
  axis?: 'horizontal' | 'vertical'
  padding?: number
  elSize?: number
  elOffsets?: [number, number]
}

const scrollIntoView = (() => {
  return function (param: ScrollIntoViewParams): void {
    param.padding = param.padding || 0
    param.axis = param.axis === 'horizontal' ? 'horizontal' : 'vertical'

    let firstOffset

    if (param.el) {
      param.elSize =
        param.axis === 'vertical' ? param.el.offsetHeight : param.el.offsetWidth

      firstOffset = getElRefOffset(
        param.el,
        param.axis === 'vertical' ? 'top' : 'left',
        param.ref,
      )
      param.elOffsets = [firstOffset, firstOffset + param.elSize]
    } else {
      // If param.el not set, param.elOffsets shoud be set instead
      const existingOffsets = param.elOffsets
      if (!existingOffsets) return
      param.elSize = existingOffsets[1] - existingOffsets[0]
    }

    const elOffsets = param.elOffsets
    const elSize = param.elSize
    if (!elOffsets || elSize === undefined) return

    scrollElIntoView(param.ref, param.axis, elOffsets, elSize, param.padding)
  }
})()

const resizeImageIfNeeded = (
  maxSize: number | null,
  sizeObj: { width: number; height: number },
): { width: number; height: number } => {
  let { width, height } = sizeObj
  if (maxSize) {
    if (width > height && width > maxSize) {
      height *= maxSize / width
      width = maxSize
    } else if (height > width && height > maxSize) {
      width *= maxSize / height
      height = maxSize
    }
  }
  return { width, height }
}

const isSafari = (): boolean =>
  /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

// Internet Explorer 6-11
const isIE = (): boolean => {
  if ('documentMode' in document) return true
  return false
}

export { scrollIntoView, resizeImageIfNeeded, isSafari, isEdge, isIE }
